import type { RefObject, PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  alertesDuMur,
  cotesDuMur,
  deplacer,
  rectangleDe,
  type Mur,
  type Placement,
  type RepereAccrochage,
} from '@artosera/shared';
import type { AlertePlan, OeuvreAccrochage } from '../../api/accrochage.js';
import styles from './Accrochage.module.css';

export function positionSurMur(svg: SVGSVGElement, mur: Mur, clientX: number, clientY: number) {
  const matrice = svg.getScreenCTM?.();
  if (matrice) {
    const inverse = matrice.inverse();
    return {
      x: inverse.a * clientX + inverse.c * clientY + inverse.e,
      y: mur.hauteurCm - (inverse.b * clientX + inverse.d * clientY + inverse.f),
    };
  }
  // Le repli respecte les marges de preserveAspectRatio, notamment dans jsdom.
  const boite = svg.getBoundingClientRect();
  const echelle = Math.min(boite.width / mur.largeurCm, boite.height / mur.hauteurCm);
  if (!(echelle > 0)) return null;
  return {
    x: (clientX - boite.left - (boite.width - mur.largeurCm * echelle) / 2) / echelle,
    y:
      mur.hauteurCm -
      (clientY - boite.top - (boite.height - mur.hauteurCm * echelle) / 2) / echelle,
  };
}
export function EditeurMur({
  mur,
  axeCm,
  placements,
  oeuvres,
  alertesServeur,
  reperes,
  svgRef,
  peutEcrire,
  onChoisir,
  onDeplacer,
  onRetirer,
  onGlisser,
}: {
  mur: Mur;
  axeCm: number;
  placements: Placement[];
  oeuvres: OeuvreAccrochage[];
  alertesServeur: AlertePlan[];
  reperes: RepereAccrochage[];
  svgRef: RefObject<SVGSVGElement | null>;
  peutEcrire: boolean;
  onChoisir: (id: string) => void;
  onDeplacer: (placement: Placement) => void;
  onRetirer: (id: string) => void;
  onGlisser: (e: PointerEvent<SVGGElement>, placement: Placement) => void;
}) {
  const { t } = useTranslation('regie');
  const rectangles = placements.map((p) => ({
    ...rectangleDe(p, oeuvres.find((o) => o.id === p.oeuvreId)?.dimensions),
    oeuvreId: p.oeuvreId,
  }));
  const alertes = [
    ...alertesDuMur(mur, rectangles),
    ...alertesServeur.filter(
      (a) => a.nature === 'hors_exposition' || a.nature === 'oeuvre_retiree',
    ),
  ];
  const cotes = cotesDuMur(mur, rectangles);
  const titre = (id: string) => oeuvres.find((o) => o.id === id)?.titre ?? id;
  const alertePour = (id: string) =>
    alertes.filter((a) => ('oeuvreIds' in a ? a.oeuvreIds.includes(id) : a.oeuvreId === id));
  return (
    <div className={styles.mur}>
      <svg
        ref={svgRef}
        className={styles.editeur}
        role="group"
        aria-label={t('accrochage.apercuMur', { nom: mur.nom })}
        viewBox={`0 0 ${mur.largeurCm} ${mur.hauteurCm}`}
      >
        <rect
          width={mur.largeurCm}
          height={mur.hauteurCm}
          fill="var(--surface)"
          stroke="var(--ink)"
        />
        {mur.zones.map((z) => (
          <g key={z._id}>
            <rect
              x={z.xCm}
              y={mur.hauteurCm - z.yCm - z.hauteurCm}
              width={z.largeurCm}
              height={z.hauteurCm}
              fill="var(--ink-soft)"
              opacity="0.3"
            />
            <text x={z.xCm + 2} y={mur.hauteurCm - z.yCm - z.hauteurCm + 12} fontSize="10">
              {z.libelle || t(`accrochage.zones.${z.nature}`)}
            </text>
          </g>
        ))}
        <line
          x1="0"
          x2={mur.largeurCm}
          y1={mur.hauteurCm - axeCm}
          y2={mur.hauteurCm - axeCm}
          stroke="var(--accent)"
          strokeDasharray="4 4"
        />
        {reperes.map((r, i) => (
          <line
            key={i}
            x1={r.direction === 'x' ? r.positionCm : 0}
            x2={r.direction === 'x' ? r.positionCm : mur.largeurCm}
            y1={r.direction === 'y' ? mur.hauteurCm - r.positionCm : 0}
            y2={r.direction === 'y' ? mur.hauteurCm - r.positionCm : mur.hauteurCm}
            stroke="var(--accent)"
            strokeWidth="2"
          />
        ))}
        {placements.map((p, i) => {
          const rect = rectangles[i]!;
          const oeuvre = oeuvres.find((o) => o.id === p.oeuvreId);
          const avertissements = alertePour(p.oeuvreId)
            .map((a) => t(`accrochage.alertes.${a.nature}`))
            .join(', ');
          return (
            <g
              key={p.oeuvreId}
              tabIndex={0}
              role="button"
              aria-label={`${t('accrochage.position', { titre: titre(p.oeuvreId), x: p.xCm, y: p.yCm })}${avertissements ? `, ${avertissements}` : ''}`}
              aria-disabled={!peutEcrire}
              className={styles.objet}
              onFocus={() => onChoisir(p.oeuvreId)}
              onClick={() => onChoisir(p.oeuvreId)}
              onPointerDown={(e) => {
                if (peutEcrire) onGlisser(e, p);
              }}
              onKeyDown={(e) => {
                if (!peutEcrire) return;
                const directions = {
                  ArrowLeft: 'gauche',
                  ArrowRight: 'droite',
                  ArrowUp: 'haut',
                  ArrowDown: 'bas',
                } as const;
                if (e.key in directions) {
                  e.preventDefault();
                  const deplace = deplacer(
                    rect,
                    directions[e.key as keyof typeof directions],
                    e.shiftKey,
                  );
                  onDeplacer({ ...p, xCm: deplace.x, yCm: deplace.y });
                }
                if (e.key === 'Delete' || e.key === 'Backspace') {
                  e.preventDefault();
                  onRetirer(p.oeuvreId);
                }
              }}
            >
              <rect
                x={rect.x}
                y={mur.hauteurCm - rect.y - rect.hauteur}
                width={rect.largeur}
                height={rect.hauteur}
                fill={rect.provisoire ? 'var(--line)' : 'var(--surface)'}
                stroke={avertissements ? 'var(--status-sold)' : 'var(--ink)'}
                strokeWidth="2"
              />
              {oeuvre?.apercuUrl && !rect.provisoire && (
                <image
                  href={oeuvre.apercuUrl}
                  x={rect.x}
                  y={mur.hauteurCm - rect.y - rect.hauteur}
                  width={rect.largeur}
                  height={rect.hauteur}
                  preserveAspectRatio="xMidYMid meet"
                  pointerEvents="none"
                />
              )}
              <text
                x={rect.x + rect.largeur / 2}
                y={mur.hauteurCm - rect.y - rect.hauteur / 2}
                textAnchor="middle"
                fontSize="10"
                pointerEvents="none"
              >
                {titre(p.oeuvreId)}
              </text>
              {rect.provisoire && (
                <text x={rect.x} y={mur.hauteurCm - rect.y - 3} fontSize="8">
                  {t('accrochage.provisoire')}
                </text>
              )}
              {avertissements && (
                <text
                  x={rect.x}
                  y={mur.hauteurCm - rect.y - rect.hauteur + 10}
                  fontSize="10"
                  aria-hidden="true"
                >
                  ⚠
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {reperes.length > 0 && (
        <p role="status">{reperes.map((r) => t(`accrochage.reperes.${r.nature}`)).join(' · ')}</p>
      )}
      <details open>
        <summary>{t('accrochage.cotes')}</summary>
        <ul>
          {cotes.oeuvres.map((c) => (
            <li key={c.oeuvreId}>
              {titre(c.oeuvreId)} —{' '}
              {t('accrochage.coteOeuvre', {
                gauche: c.gaucheCm,
                droite: c.droiteCm,
                bas: c.basCm,
                centre: c.centreCm,
              })}
            </li>
          ))}
          {cotes.espacements.map((c, i) => (
            <li key={i}>
              {c.oeuvreIds.map(titre).join(' ↔ ')} : {c.distanceCm} cm
            </li>
          ))}
        </ul>
      </details>
      {alertes.length > 0 && (
        <div>
          <h3>{t('accrochage.avertissements')}</h3>
          <ul>
            {alertes.map((a, i) => (
              <li key={i}>
                {t(`accrochage.alertes.${a.nature}`)} —{' '}
                {('oeuvreIds' in a ? a.oeuvreIds : [a.oeuvreId]).map(titre).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
